import type { RootOptions } from 'react-dom/client';
import { reactErrorHandler } from '@sentry/react';
import { type InternoErroBody, apiInternoErroPost } from '@/features/erro/api/apiInternoErroPost';

type ReactErrorKind = Extract<InternoErroBody, { type: 'REACT_ERROR' }>['kind'];
type CaughtErrorInfo = Parameters<NonNullable<RootOptions['onCaughtError']>>[1];
type RecoverableErrorInfo = Parameters<NonNullable<RootOptions['onRecoverableError']>>[1];
type UncaughtErrorInfo = Parameters<NonNullable<RootOptions['onUncaughtError']>>[1];
type ReactErrorInfo = CaughtErrorInfo | RecoverableErrorInfo | UncaughtErrorInfo;

const ERROR_REPORT_INTERVAL_MS = 500;

let lastErrorReportAt: number | undefined;
const captureReactError = reactErrorHandler();

function canReportError(): boolean {
    const now = Date.now();

    if (lastErrorReportAt !== undefined && now - lastErrorReportAt < ERROR_REPORT_INTERVAL_MS) {
        return false;
    }

    lastErrorReportAt = now;
    return true;
}

function getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
        return error.message;
    }

    if (typeof error === 'string') {
        return error;
    }

    try {
        return JSON.stringify(error) ?? String(error);
    } catch {
        return String(error);
    }
}

function shouldIgnoreError(message: string): boolean {
    const ignoredErrors = [
        /failed to fetch/i,
        /unable to preload css/i,
        /network\s*error/i,
        /Importing a module script failed/i,
        /undefined is not an object \(evaluating ['"]window\._META\.IABInnerFrame['"]\)/,
    ];

    return ignoredErrors.some((ignoredError) => ignoredError.test(message));
}

export async function sendReactError(
    kind: ReactErrorKind,
    error: unknown,
    errorInfo: ReactErrorInfo,
): Promise<void> {
    const message = getErrorMessage(error);

    if (shouldIgnoreError(message)) {
        return;
    }

    captureReactError(error, errorInfo);

    if (!canReportError()) {
        console.error('sendReactError', 'Erro ignorado pelo limite de envio', {
            error,
            errorInfo,
            kind,
        });

        return;
    }

    const body: InternoErroBody = {
        component_stack: errorInfo.componentStack,
        error_boundary:
            'errorBoundary' in errorInfo ? errorInfo.errorBoundary?.constructor.name : undefined,
        kind,
        message,
        name: error instanceof Error ? error.name : undefined,
        stack: error instanceof Error ? error.stack : undefined,
        type: 'REACT_ERROR',
    };

    try {
        await apiInternoErroPost(body);
        console.info('sendReactError', 'Erro enviado com sucesso', { error, errorInfo, kind });
    } catch (reportError) {
        console.error('sendReactError', 'Falha ao enviar erro', reportError);
    }
}
